import { BarChart } from "@mantine/charts";
import { Stack, Table, Text } from "@mantine/core";
import { formatCompact, formatMoney, formatPercent } from "../../lib/format";
import { useI18n } from "../../i18n/context";

/** The subset of fare fields the waterfall needs (trip pricingDetail or a sum). */
export interface MoneyParts {
  riderPaid: number;
  grossFare: number;
  driverEarning: number;
  vatOnCommission: number;
  vatOnBookingFee: number;
  gatewayFee: number;
  platformEarning: number;
  platformCommission: number;
  bookingFee: number;
  /** Rider-paid LATRA levy (pass-through to LATRA). 0 before it existed. */
  latraLevy?: number;
  roundingDifference: number;
  discount: number;
  tolls: number;
  /** Itemised parts of the fare (trip view only; absent on older trips). */
  baseCharge?: number;
  distanceCharge?: number;
  timeCharge?: number;
  /** Pickup waiting charge — already part of the fare. Trip view only. */
  waitingFee?: number;
  /** Earlier rider cancellation fee(s) collected on this trip. Trip view only. */
  cancellationFee?: number;
}

/**
 * Where the rider's money went: driver → VAT (on commission, on booking fee)
 * → LATRA levy → gateway → rounding → what Safiri keeps. The residual "rounding" makes
 * the bars add up exactly, so the chart never hides an unexplained gap. A
 * fully itemized table (base fare, booking fee, commission, discount) sits
 * under the chart, showing how each top-level figure is built.
 */
export function MoneyWaterfall({ m, height = 260 }: { m: MoneyParts; height?: number }) {
  const { t } = useI18n();
  const vat = m.vatOnCommission + m.vatOnBookingFee;
  const waitingFee = m.waitingFee ?? 0;
  // Collected on behalf of an earlier, cancelled trip: in what the rider paid
  // but not this trip's fare, driver or Smart Safiri figures.
  const cancellationFee = m.cancellationFee ?? 0;
  const latraLevy = m.latraLevy ?? 0;
  // Actual (metered) fare: the rounding has its own row, like the rider's receipt.
  const fare =
    m.grossFare -
    m.tolls -
    m.gatewayFee -
    m.bookingFee -
    latraLevy -
    cancellationFee -
    m.roundingDifference;
  const rounding =
    m.riderPaid -
    m.driverEarning -
    vat -
    latraLevy -
    m.gatewayFee -
    m.platformEarning -
    cancellationFee;
  const itemised = m.baseCharge != null;
  const key = "amount";

  // Short axis labels; the table below uses the full names.
  const steps = [
    { label: t("fin.short.paid"), value: m.riderPaid, color: "var(--viz-cat-2)" },
    { label: t("fin.short.driver"), value: -m.driverEarning, color: "var(--accent-driver-solid)" },
    { label: t("fin.short.vatCommission"), value: -m.vatOnCommission, color: "var(--viz-cat-3)" },
    { label: t("fin.short.vatBooking"), value: -m.vatOnBookingFee, color: "var(--viz-cat-3)" },
    ...(latraLevy ? [{ label: t("fin.short.latra"), value: -latraLevy, color: "var(--viz-cat-6)" }] : []),
    ...(m.gatewayFee ? [{ label: t("fin.short.gateway"), value: -m.gatewayFee, color: "var(--viz-cat-4)" }] : []),
    ...(cancellationFee
      ? [{ label: t("fin.short.cancellationFee"), value: -cancellationFee, color: "var(--viz-cat-5)" }]
      : []),
    ...(Math.abs(rounding) >= 1 ? [{ label: t("fin.short.rounding"), value: -rounding, color: "var(--text-disabled)" }] : []),
  ];
  // BarChart's waterfall mutates the rows, so build them fresh every render.
  const data = [
    ...steps.map((s) => ({ step: s.label, [key]: s.value, color: s.color })),
    { step: t("fin.short.safiri"), [key]: m.platformEarning, color: "var(--action-primary-bg)", standalone: true },
  ];

  const pct = (v: number) => (m.riderPaid ? formatPercent(v / m.riderPaid) : "—");

  return (
    <Stack gap="sm">
      <BarChart
        h={height}
        data={data}
        dataKey="step"
        type="waterfall"
        series={[{ name: key, label: t("fin.amount"), color: "var(--viz-cat-2)" }]}
        valueFormatter={(v) => formatCompact(v)}
        // Bar value labels would show running totals, not each step's amount —
        // the table below carries the exact figures instead.
        xAxisProps={{ interval: 0, tick: { fontSize: 11 } }}
        gridAxis="y"
        tickLine="none"
        aria-label={t("fin.waterfallLabel", {
          paid: formatMoney(m.riderPaid),
          kept: formatMoney(m.platformEarning),
        })}
      />
      <Table fz="sm" withRowBorders={false} verticalSpacing={4}>
        <Table.Tbody>
          <Row label={t("fin.riderPaid")} value={m.riderPaid} pct="100%" strong />
          {itemised ? (
            <>
              <Row label={t("fin.baseFareRow")} value={m.baseCharge ?? 0} pct="" sub />
              <Row label={t("fin.distanceRow")} value={m.distanceCharge ?? 0} pct="" sub />
              <Row label={t("fin.timeRow")} value={m.timeCharge ?? 0} pct="" sub />
              {waitingFee ? <Row label={t("fin.waitingTimeRow")} value={waitingFee} pct="" sub /> : null}
            </>
          ) : (
            <>
              <Row label={t("fin.fareRow")} value={fare} pct="" sub />
              {waitingFee ? <Row label={t("fin.waitingFeeRow")} value={waitingFee} pct="" sub /> : null}
            </>
          )}
          <Row label={t("fin.bookingFeeRow")} value={m.bookingFee} pct="" sub />
          {latraLevy ? <Row label={t("fin.latraLevyRow")} value={latraLevy} pct="" sub /> : null}
          {m.tolls ? <Row label={t("fin.tollsRow")} value={m.tolls} pct="" sub /> : null}
          {m.gatewayFee ? <Row label={t("fin.gatewayFeeRow")} value={m.gatewayFee} pct="" sub /> : null}
          {Math.abs(m.roundingDifference) >= 0.005 ? (
            <Row label={t("fin.roundingRow")} value={m.roundingDifference} pct="" sub />
          ) : null}
          {m.discount ? <Row label={t("fin.discountRow")} value={-m.discount} pct="" sub /> : null}
          {cancellationFee ? <Row label={t("fin.cancellationFeeRow")} value={cancellationFee} pct="" sub /> : null}

          <Row label={t("fin.toDriver")} hint={m.tolls ? t("fin.inclTolls", { v: formatMoney(m.tolls) }) : undefined} value={-m.driverEarning} pct={pct(m.driverEarning)} />
          <Row label={t("fin.commissionRow")} value={-m.platformCommission} pct="" sub />
          <Row label={t("fin.vatOnCommissionRow")} value={-m.vatOnCommission} pct="" sub />

          {latraLevy ? <Row label={t("fin.toLatra")} value={-latraLevy} pct={pct(latraLevy)} /> : null}
          {m.gatewayFee ? <Row label={t("fin.toGateway")} value={-m.gatewayFee} pct={pct(m.gatewayFee)} /> : null}
          {cancellationFee ? (
            <Row label={t("fin.toCancelledTrip")} value={-cancellationFee} pct={pct(cancellationFee)} />
          ) : null}
          {Math.abs(rounding) >= 1 ? <Row label={t("fin.rounding")} value={-rounding} pct={pct(rounding)} /> : null}

          <Row label={t("fin.safiriEarned")} value={m.platformEarning} pct={pct(m.platformEarning)} strong />
          <Row label={t("fin.commissionRow")} value={m.platformCommission} pct="" sub />
          <Row label={t("fin.bookingFeeRow")} value={m.bookingFee} pct="" sub />
          {m.discount ? <Row label={t("fin.discountRow")} value={-m.discount} pct="" sub /> : null}
          <Row label={t("fin.vatOnBookingFeeRow")} value={-m.vatOnBookingFee} pct="" sub />
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

function Row({
  label,
  hint,
  value,
  pct,
  strong,
  sub,
}: {
  label: string;
  hint?: string;
  value: number;
  pct: string;
  strong?: boolean;
  sub?: boolean;
}) {
  return (
    <Table.Tr>
      <Table.Td pl={sub ? 24 : undefined}>
        <Text size={sub ? "xs" : "sm"} c={sub ? "dimmed" : undefined} fw={strong ? 650 : 400}>
          {label}
        </Text>
        {hint && (
          <Text size="xs" c="dimmed">
            {hint}
          </Text>
        )}
      </Table.Td>
      <Table.Td ta="right" fw={strong ? 650 : 400} className="num" c={sub ? "dimmed" : undefined}>
        {formatMoney(value)}
      </Table.Td>
      <Table.Td ta="right" c="dimmed" w={70} className="num">
        {sub ? "" : pct}
      </Table.Td>
    </Table.Tr>
  );
}
